import type { AllComponents } from "../../../../components/_default/interfaces/base";
import { resolveComponentMapKey } from "./structureIdentity";
import type { StructureMap } from "./structureReducer";

export type LayoutRootKind = "header" | "footer";

/** True when a top-level root should be treated as the page footer (type or legacy class). */
export function isFooterRootComponent(
  components: StructureMap["components"],
  rootRef: string
): boolean {
  const key = resolveComponentMapKey(components, rootRef) ?? rootRef;
  const comp = components[key];
  if (!comp) {
    return false;
  }
  if (comp.type === "footer") {
    return true;
  }
  const classes = String(comp.attributes?.classes ?? "");
  return classes.split(/\s+/).includes("builder-footer");
}

export function indexOfFooterRoot(
  roots: string[],
  components: StructureMap["components"]
): number {
  return roots.findIndex((r) => isFooterRootComponent(components, r));
}

/** Merges a new top-level root into `webpageComponents`. Keeps footer last when appending. */
export function mergeRootIntoWebpageComponents(
  currentRoots: string[],
  rootKey: string,
  rootType: AllComponents["type"],
  sortOrder?: number,
  components?: StructureMap["components"]
): string[] {
  const rootsWithoutInjected = currentRoots.filter((root) => root !== rootKey);
  const shouldPlaceFirst = rootType === "header" || (sortOrder ?? 0) < 0;
  if (shouldPlaceFirst) {
    return [rootKey, ...rootsWithoutInjected];
  }
  if (rootType === "footer") {
    return [...rootsWithoutInjected, rootKey];
  }
  if (components) {
    const footerIdx = indexOfFooterRoot(rootsWithoutInjected, components);
    if (footerIdx >= 0) {
      const next = [...rootsWithoutInjected];
      next.splice(footerIdx, 0, rootKey);
      return next;
    }
  }
  return [...rootsWithoutInjected, rootKey];
}

/** All webpage roots that qualify as footers (canonical footer type or `builder-footer` class). */
export function listFooterLikeRoots(
  roots: string[],
  components: StructureMap["components"]
): string[] {
  return roots.filter((root) => isFooterRootComponent(components, root));
}

function ensureClassToken(classes: string, token: string): string {
  const tokens = classes
    .split(/\s+/)
    .map((x) => x.trim())
    .filter((x) => x !== "");
  if (!tokens.includes(token)) {
    tokens.push(token);
  }
  return tokens.join(" ");
}

function removeClassToken(classes: string, token: string): string {
  return classes
    .split(/\s+/)
    .map((x) => x.trim())
    .filter((x) => x !== "" && x !== token)
    .join(" ");
}

/** Normalizes a layout template root so injected page structure behaves as header/footer layout root. */
export function normalizeLayoutRootForInjection(
  root: AllComponents,
  kind: LayoutRootKind
): AllComponents {
  const nextType = kind === "footer" ? "footer" : "header";
  const currentClasses = String(root.attributes?.classes ?? "");
  const nextClasses =
    kind === "footer"
      ? ensureClassToken(currentClasses, "builder-footer")
      : removeClassToken(currentClasses, "builder-footer");

  return {
    ...root,
    type: nextType,
    attributes: {
      ...(root.attributes ?? {}),
      classes: nextClasses,
    },
  };
}
